/**
 * Dense hierarchical scope tree.
 *
 * Intentionally NOT MUI's TreeView — its default padding is too generous
 * for the spec's VS-Code-explorer aesthetic (§3.2). Hand-rolled so the
 * row height (22px), indent (12px), and font size (12px) are exact.
 *
 * Renders the same data shape in two modes:
 *   - 'in-scope'   → checked rows, click removes from scope
 *   - 'available'  → unchecked rows, click adds to scope
 *
 * Models and instances are toggleable expand/collapse; scenarios are
 * leaves.
 */
import React, { useState, useCallback } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import {
  ArrowDropDown,
  ArrowRight,
  CheckBox,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';

import {
  ScopeTreeInstance,
  ScopeTreeModel,
  ScopeTreeScenario,
  scenarioKey,
} from './types';

export type ScopeTreeMode = 'in-scope' | 'available';

interface ScopeTreeProps {
  tree: ScopeTreeModel[];
  mode: ScopeTreeMode;
  pendingKeys: Set<string>;
  onToggleScenario: (sc: ScopeTreeScenario) => void;
  disabled?: boolean;
  /**
   * When set, model + instance nodes are auto-expanded if any
   * descendant scenario name / instance name / model name / tag
   * matches the filter. Set by ScopePanel based on the Available
   * filter input.
   */
  filterText?: string;
  /**
   * When true, all model + instance nodes start expanded regardless
   * of mode/filter. Set by ScopePanel for the in-scope tree (always)
   * and for the available tree when scope is empty or the tree is
   * small enough that hiding nodes adds clicks without saving real
   * estate.
   */
  defaultExpandAll?: boolean;
}

// VS-Code-explorer density (spec §3.2)
const FONT_SIZE = 12;
const ROW_HEIGHT = 22;
const INDENT_PX = 12;

export const ScopeTree: React.FC<ScopeTreeProps> = ({
  tree,
  mode,
  pendingKeys,
  onToggleScenario,
  disabled = false,
  filterText,
  defaultExpandAll = false,
}) => {
  if (tree.length === 0) return null;
  return (
    <Box sx={{ fontSize: FONT_SIZE, lineHeight: 1.4 }}>
      {tree.map((model) => (
        <ModelNode
          key={model.forecast_model_id}
          model={model}
          mode={mode}
          pendingKeys={pendingKeys}
          onToggleScenario={onToggleScenario}
          disabled={disabled}
          filterText={filterText}
          defaultExpandAll={defaultExpandAll}
        />
      ))}
    </Box>
  );
};

interface ModelNodeProps {
  model: ScopeTreeModel;
  mode: ScopeTreeMode;
  pendingKeys: Set<string>;
  onToggleScenario: (sc: ScopeTreeScenario) => void;
  disabled: boolean;
  filterText?: string;
  defaultExpandAll: boolean;
}

const ModelNode: React.FC<ModelNodeProps> = ({
  model,
  mode,
  pendingKeys,
  onToggleScenario,
  disabled,
  filterText,
  defaultExpandAll,
}) => {
  // Default expand: in-scope tree always expanded; available tree
  // expands when filtering or when the panel tells us to.
  const filtering = !!(filterText && filterText.trim().length > 0);
  const defaultExpanded = mode === 'in-scope' || filtering || defaultExpandAll;
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box>
      <RowChrome
        depth={0}
        caret={expanded ? 'down' : 'right'}
        onClick={() => setExpanded((e) => !e)}
        label={model.forecast_model_name}
        tooltip={model.forecast_model_name}
      />
      {expanded && (
        <Box>
          {model.instances.map((inst) => (
            <InstanceNode
              key={inst.model_instance_id}
              instance={inst}
              mode={mode}
              pendingKeys={pendingKeys}
              onToggleScenario={onToggleScenario}
              disabled={disabled}
              filterText={filterText}
              defaultExpandAll={defaultExpandAll}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

interface InstanceNodeProps {
  instance: ScopeTreeInstance;
  mode: ScopeTreeMode;
  pendingKeys: Set<string>;
  onToggleScenario: (sc: ScopeTreeScenario) => void;
  disabled: boolean;
  filterText?: string;
  defaultExpandAll: boolean;
}

const InstanceNode: React.FC<InstanceNodeProps> = ({
  instance,
  mode,
  pendingKeys,
  onToggleScenario,
  disabled,
  filterText,
  defaultExpandAll,
}) => {
  const filtering = !!(filterText && filterText.trim().length > 0);
  const defaultExpanded = mode === 'in-scope' || filtering || defaultExpandAll;
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box>
      <RowChrome
        depth={1}
        caret={expanded ? 'down' : 'right'}
        onClick={() => setExpanded((e) => !e)}
        label={instance.model_instance_name}
        tooltip={`${instance.forecast_model_name} / ${instance.model_instance_name}`}
        suffix={!expanded ? `(${instance.scenarios.length})` : undefined}
      />
      {expanded && (
        <Box>
          {instance.scenarios.map((sc) => (
            <ScenarioRow
              key={sc.scenario_id}
              scenario={sc}
              mode={mode}
              pending={pendingKeys.has(
                scenarioKey(sc.model_instance_id, sc.scenario_id)
              )}
              onClick={() => onToggleScenario(sc)}
              disabled={disabled}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

interface ScenarioRowProps {
  scenario: ScopeTreeScenario;
  mode: ScopeTreeMode;
  pending: boolean;
  onClick: () => void;
  disabled: boolean;
}

const ScenarioRow: React.FC<ScenarioRowProps> = ({
  scenario,
  mode,
  pending,
  onClick,
  disabled,
}) => {
  const checked = mode === 'in-scope';
  const tooltipText =
    `${scenario.forecast_model_name} / ${scenario.model_instance_name} / ${scenario.scenario_name}` +
    (scenario.tag ? ` · ${scenario.tag}` : '');
  const ariaLabel = `${scenario.scenario_name}, in instance ${scenario.model_instance_name} of model ${scenario.forecast_model_name}${scenario.tag ? `, tagged ${scenario.tag}` : ''}, ${
    checked
      ? 'currently in scope (click to remove)'
      : 'available (click to add to scope)'
  }`;

  const handleKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled || pending) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onClick();
      }
    },
    [disabled, pending, onClick]
  );

  return (
    <Tooltip title={tooltipText} enterDelay={500} placement="right">
      <Box
        role="checkbox"
        aria-checked={checked}
        aria-label={ariaLabel}
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (!disabled && !pending) onClick();
        }}
        onKeyDown={handleKey}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: ROW_HEIGHT,
          pl: `${INDENT_PX * 2 + 4}px`,
          pr: 0.5,
          cursor: disabled || pending ? 'default' : 'pointer',
          userSelect: 'none',
          color: disabled ? 'text.disabled' : 'text.primary',
          '&:hover': {
            bgcolor: disabled || pending ? 'transparent' : 'action.hover',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '-2px',
          },
        }}
      >
        <CheckboxGlyph checked={checked} pending={pending} />
        <Typography
          component="span"
          noWrap
          sx={{
            fontSize: FONT_SIZE,
            lineHeight: 1.4,
            ml: 0.5,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {scenario.scenario_name}
          {scenario.tag ? (
            <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
              · {scenario.tag}
            </Box>
          ) : null}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const CheckboxGlyph: React.FC<{ checked: boolean; pending: boolean }> = ({
  checked,
  pending,
}) => {
  if (pending) {
    return <CircularProgress size={12} thickness={6} sx={{ ml: '1px' }} />;
  }
  const sx = {
    fontSize: 14,
    color: checked ? 'primary.main' : 'text.secondary',
  };
  return checked ? <CheckBox sx={sx} /> : <CheckBoxOutlineBlank sx={sx} />;
};

interface RowChromeProps {
  depth: number;
  caret: 'down' | 'right';
  onClick: () => void;
  label: string;
  tooltip: string;
  suffix?: string;
}

const RowChrome: React.FC<RowChromeProps> = ({
  depth,
  caret,
  onClick,
  label,
  tooltip,
  suffix,
}) => (
  <Tooltip title={tooltip} enterDelay={500} placement="right">
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: ROW_HEIGHT,
        pl: `${depth * INDENT_PX}px`,
        pr: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ width: 12, display: 'flex', alignItems: 'center' }}>
        {caret === 'down' ? (
          <ArrowDropDown sx={{ fontSize: 14 }} />
        ) : (
          <ArrowRight sx={{ fontSize: 14 }} />
        )}
      </Box>
      <Typography
        component="span"
        noWrap
        sx={{
          fontSize: FONT_SIZE,
          lineHeight: 1.4,
          fontWeight: 500,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>
      {suffix && (
        <Typography
          component="span"
          sx={{
            fontSize: 11,
            color: 'text.secondary',
            ml: 0.5,
          }}
        >
          {suffix}
        </Typography>
      )}
    </Box>
  </Tooltip>
);
